/**
 * Live Chat assistant — SIMULATED.
 *
 * This is a stand-in for the real AI backend. Keep the
 * `(history) => Promise<LiveChatReply>` contract stable; when the AI service is
 * ready, replace the body of {@link sendLiveChatMessage} with the real call
 * (streaming optional) and everything in `SupportLiveChat` keeps working.
 */

export interface LiveChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface LiveChatReply {
  content: string;
  /** When true, the UI offers to open a new support request. */
  suggestTicket?: boolean;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const CANNED: Array<{ test: RegExp; reply: string; suggestTicket?: boolean }> = [
  {
    test: /\b(hi|hello|hey|good (morning|afternoon|evening))\b/,
    reply: "Hi! 👋 I'm your community assistant. Ask me about bookings, billing, maintenance, access or anything estate-related.",
  },
  {
    test: /\b(water|leak|burst|pipe|geyser|no water)\b/,
    reply:
      "Sounds like a water issue. If it's an active leak, please close the nearest stopcock if you safely can. I can log a maintenance request and notify the estate team right away — want me to do that?",
    suggestTicket: true,
  },
  {
    test: /\b(electric|power|outage|load ?shedding|tripped|lights?)\b/,
    reply:
      "For power problems, first check whether it's a scheduled load-shedding slot. If the issue is on your property (e.g. a tripped DB), I can raise a request with maintenance. Shall I log it?",
    suggestTicket: true,
  },
  {
    test: /\b(book|booking|facility|clubhouse|tennis|braai|venue)\b/,
    reply:
      "You can reserve facilities under Book → Facilities. Tell me the facility and date and I'll point you to the right slot. Want me to open Facilities for you?",
  },
  {
    test: /\b(levy|invoice|account|balance|statement|pay|payment)\b/,
    reply:
      "For accounts, your latest statement and balance are under Account → Invoices. If a payment isn't reflecting, I can flag it to the finance team for you.",
    suggestTicket: true,
  },
  {
    test: /\b(visitor|guest|access|gate|invite|code)\b/,
    reply:
      "You can pre-authorise visitors under Access invites — generate a code or QR and share it. The gate will recognise it on arrival. Want me to take you there?",
  },
  {
    test: /\b(refuse|bin|garbage|rubbish|waste|collection|recycl)\b/,
    reply:
      "Refuse collection runs on the estate's published schedule. If a collection was missed, I can log it so the team follows up with the municipality.",
    suggestTicket: true,
  },
  {
    test: /\b(thanks|thank you|cheers|great|awesome)\b/,
    reply: "You're welcome! Anything else I can help with?",
  },
];

const FALLBACKS = [
  "Got it — I've noted that. A team member can pick this up; would you like me to log it as a support request so it's tracked?",
  "Thanks for the detail. I can help with bookings, billing, maintenance and access. If this needs a person, I can open a support request for you.",
  "Understood. Want me to log this as a support request so the estate team can follow up?",
];

/**
 * Simulated assistant reply. Replace with the real AI backend later.
 */
export async function sendLiveChatMessage(history: LiveChatMessage[]): Promise<LiveChatReply> {
  const lastUser = [...history].reverse().find((m) => m.role === 'user')?.content?.toLowerCase() ?? '';

  // Simulate network + "thinking" latency for a live feel.
  await delay(650 + Math.floor(Math.random() * 900));

  const match = CANNED.find((c) => c.test.test(lastUser));
  if (match) return { content: match.reply, suggestTicket: match.suggestTicket };

  const fallback = FALLBACKS[Math.floor(Math.random() * FALLBACKS.length)] ?? FALLBACKS[0]!;
  return { content: fallback, suggestTicket: true };
}
